import type { Voice, VoiceId } from "@contract";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CREDIT_RATE_BY_MODEL } from "../../../../config/constants";
import { errorCodeOf } from "../../../api/fetcher";
import { AudioProvider } from "../../../components/audio/AudioProvider";
import { PlayButton } from "../../../components/audio/PlayButton";
import { TimeReadout } from "../../../components/audio/TimeReadout";
import { WaveformScrubber } from "../../../components/audio/WaveformScrubber";
import { Chip } from "../../../components/control/Chip";
import { Select } from "../../../components/control/Select";
import { Card } from "../../../components/data/Card";
import { Banner } from "../../../components/feedback/Banner";
import { EmptyState } from "../../../components/feedback/EmptyState";
import { ErrorBanner } from "../../../components/feedback/ErrorBanner";
import { ErrorState } from "../../../components/feedback/ErrorState";
import { Skeleton } from "../../../components/feedback/Skeleton";
import { Num } from "../../../components/format/Num";
import { DraftActions } from "../../../drafts/DraftActions";
import { useWorkspaceDraft } from "../../../drafts/useWorkspaceDraft";
import { useBookDetail } from "../bookContext";
import { renderSettingsLocked } from "../render/logic";
import { useCasting, useSampleParagraph, useVoiceSamples } from "./useCasting";

import { RecommendedVoices, VoiceLibrary } from "./VoiceLibrary";

export function CastingScreen() {
  const book = useBookDetail();
  return (
    <AudioProvider key={book.id}>
      <CastingContent />
    </AudioProvider>
  );
}

function CastingContent() {
  const { t } = useTranslation(["casting", "common", "components"]);
  const book = useBookDetail();
  const data = useCasting();
  const draft = useWorkspaceDraft(
    book.id,
    "casting",
    { voiceId: data.casting.data?.narrator?.id ?? null },
    !data.casting.isPending,
  );
  const selectedId = draft.value.voiceId;
  const setSelectedId = (voiceId: VoiceId) => draft.setValue({ voiceId });
  const chapters = book.chapters
    .filter((chapter) => !chapter.excluded)
    .sort((a, b) => a.position - b.position);
  const [imported, setImported] = useState<Voice | null>(null);
  const [tryVoice, setTryVoice] = useState<string | null>(null);
  const [chosenChapter, setChosenChapter] = useState<string | undefined>();
  const chapterId = chapters.some((chapter) => chapter.id === chosenChapter)
    ? chosenChapter
    : (chapters.find((c) => c.id === book.calibrationChapterId)?.id ?? chapters[0]?.id);
  const paragraph = useSampleParagraph(chapterId);
  const voices = data.voices.data?.voices ?? [];
  const selected =
    voices.find((voice) => voice.id === selectedId) ??
    (imported?.id === selectedId ? imported : undefined) ??
    voices.find((voice) => voice.id === data.casting.data?.narrator?.id) ??
    voices.find((voice) => voice.isDefaultForLanguage) ??
    voices[0];
  const samples = useVoiceSamples(book, paragraph.data, selected?.id, selected?.rate);
  useEffect(() => {
    if (tryVoice && selected?.id === tryVoice && paragraph.data && !samples.busy) {
      setTryVoice(null);
      samples.play();
    }
  }, [tryVoice, selected?.id, paragraph.data, samples.busy, samples.play]);
  const selectVoice = (voice: Voice, tryBook = false) => {
    setImported(voice);
    setSelectedId(voice.id);
    if (tryBook) setTryVoice(voice.id);
  };
  const locked = renderSettingsLocked(book) || data.casting.data?.locked === true;
  const saved = selected?.id === data.casting.data?.narrator?.id;
  const error = data.voices.error ?? data.casting.error;
  const modelText = book.ttsModel === "eleven_v3" ? t("casting:modelV3") : t("casting:modelV2");

  if (!data.language) return <EmptyState title={t("casting:languageMissing")} />;
  if (data.voices.isPending || data.casting.isPending) return <Skeleton variant="text" lines={6} />;
  if (error && voices.length === 0)
    return (
      <ErrorState
        code={errorCodeOf(error)}
        onRetry={() => {
          void data.voices.refetch();
          void data.casting.refetch();
        }}
      />
    );

  return (
    <div className="flex flex-col gap-4" data-screen="casting">
      <ErrorBanner error={error ?? draft.error} />
      {!saved && selected ? (
        <Banner tone="info">{t("casting:suggested", { name: selected.name })}</Banner>
      ) : null}
      {locked ? <Banner tone="info">{t("casting:locked")}</Banner> : null}
      <div className="grid min-w-0 gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
        <Card
          title={t("casting:title")}
          titleAs="h1"
          description={t("casting:description")}
          padding="none"
          className="order-2 min-w-0 xl:order-1"
        >
          <div className="flex flex-wrap gap-2 px-6 pb-3">
            <Chip tone="accent" label={t(`common:bookLanguage.${data.language}`)} />
            <Chip label={t("casting:oneNarrator")} />
          </div>
          <RecommendedVoices
            voices={voices}
            selectedId={selected?.id}
            disabled={locked || draft.applying || !draft.loaded}
            onSelect={selectVoice}
            onTry={(voice) => selectVoice(voice, true)}
          />
          <VoiceLibrary
            key={`${book.id}:${data.language}`}
            bookId={book.id}
            language={data.language}
            model={book.ttsModel}
            disabled={locked || draft.applying || !draft.loaded}
            selectedId={selected?.elevenlabsVoiceId}
            onSelect={selectVoice}
          />
          <DraftActions
            draft={draft}
            label={t("nav:workspace.applyVoice")}
            disabled={locked || !selected || saved}
            always
            onApply={() => {
              if (selected) {
                draft.setValue({ voiceId: selected.id });
                void draft.apply().catch(() => {});
              }
            }}
          >
            <p>
              {selected?.name} · {locked ? t("casting:lockedShort") : t("casting:untilAudio")}
            </p>
          </DraftActions>
        </Card>
        <Card
          label={t("casting:sample")}
          className="order-1 min-w-0 self-start xl:sticky xl:top-4 xl:order-2 xl:max-h-[70dvh] xl:overflow-y-auto"
          padding="regular"
        >
          <p className="mb-3 font-medium">{t("casting:library.tryBook")}</p>
          <p className="mb-3 text-data">
            {selected?.rate === null
              ? t("casting:library.unknownRate")
              : t("casting:library.rate", { rate: selected?.rate ?? 1 })}
          </p>
          {paragraph.data && selected?.rate != null ? (
            <p className="mb-3 text-data text-ink-2">
              {t("casting:library.sampleEstimate", {
                credits: Math.ceil(
                  [...paragraph.data.text].length *
                    selected.rate *
                    CREDIT_RATE_BY_MODEL[book.ttsModel],
                ),
              })}
            </p>
          ) : null}
          <Select
            label={t("casting:sampleChapter")}
            value={chapterId ?? ""}
            disabled={chapters.length === 0}
            options={chapters.map((chapter) => ({
              value: chapter.id,
              label: `${chapter.number ?? chapter.position} ${chapter.title}`,
            }))}
            onChange={(event) => setChosenChapter(event.target.value)}
          />
          {paragraph.isPending && chapterId ? (
            <div className="py-4">
              <Skeleton variant="text" lines={4} />
            </div>
          ) : null}
          <ErrorBanner error={paragraph.error} />
          {paragraph.data ? (
            <p className="my-4 max-h-64 overflow-y-auto whitespace-pre-wrap rounded-sm border border-line-soft bg-sunken px-4 py-3 font-serif text-book-sm">
              {samples.sample?.text ?? paragraph.data.text}
            </p>
          ) : !paragraph.isPending ? (
            <p className="py-4 text-ink-2">{t("casting:noParagraph")}</p>
          ) : null}
          <div className="flex min-w-0 flex-wrap items-center gap-3 py-2">
            <PlayButton
              status={samples.status}
              disabled={
                !selected ||
                (selected.rate === null && samples.sample?.status !== "cached") ||
                !paragraph.data ||
                samples.status === "loading"
              }
              onClick={samples.play}
            />
            {samples.sample?.peaks ? (
              <WaveformScrubber
                peaks={samples.sample.peaks}
                progress={
                  samples.playback.durationMs > 0 && samples.active
                    ? samples.playback.positionMs / samples.playback.durationMs
                    : 0
                }
                {...(samples.active ? { onSeek: samples.playback.seek } : {})}
                className="min-w-24"
              />
            ) : (
              <span className="text-ink-2">
                {samples.status === "loading" ? t("casting:creating") : t("casting:createHint")}
              </span>
            )}
            {samples.sample?.durationMs != null ? (
              <TimeReadout
                positionMs={samples.active ? samples.playback.positionMs : 0}
                durationMs={samples.sample.durationMs}
              />
            ) : null}
          </div>
          <p className="py-2 font-mono text-label text-ink-2">
            {selected?.name} · {modelText}
            {samples.sample ? (
              <>
                {" "}
                · <Num value={samples.sample.characterCount} /> {t("casting:characters")}
              </>
            ) : null}
          </p>
          <ErrorBanner error={samples.error} />
          {samples.sample?.status === "failed" ? (
            <Banner tone="danger">
              {samples.sample.failure
                ? t(`errors:${samples.sample.failure.key}`)
                : t("casting:sampleFailed")}
            </Banner>
          ) : null}
          {samples.sample?.status === "cached" ? (
            <p className="text-ok" role="status">
              {t("casting:cached")}
            </p>
          ) : null}
          {samples.history.length > 0 ? (
            <div className="mt-4 border-t border-line-soft pt-3">
              <p className="label-mono">{t("casting:heard")}</p>
              <ul className="mt-2 flex flex-col gap-1">
                {samples.history.map((sample) => (
                  <li key={sample.id} className="flex justify-between gap-3 font-mono text-label">
                    <span>{sample.voice.name}</span>
                    <span className="text-ink-2">
                      {
                        {
                          cached: t("casting:sampleStatus.cached"),
                          rendering: t("casting:sampleStatus.rendering"),
                          failed: t("casting:sampleStatus.failed"),
                        }[sample.status]
                      }
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <p className="mt-4 border-t border-line pt-4 leading-relaxed text-ink-2">
            {t("casting:sampleCost")}
          </p>
        </Card>
      </div>
    </div>
  );
}
